import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';

class TestimonialTabs extends Component {
  #abort = new AbortController();
  #activeTabId = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;

    const rail = this.querySelector('[data-rail]');
    if (rail) {
      this.#panels.forEach((panel) => {
        const tpl = panel.querySelector('template[data-rail-button]');
        if (tpl) rail.appendChild(tpl.content.cloneNode(true));
      });
    }

    this.#triggers.forEach((btn) => {
      btn.addEventListener('click', this.#onClick, { signal });
      btn.addEventListener('keydown', this.#onKeydown, { signal });
    });

    this.#activeTabId =
      this.#panels.find((p) => p.dataset.active === 'true')?.dataset.tabId ?? this.#panels[0]?.dataset.tabId ?? null;

    // Bring the active logo into view on load (it may be off-screen in the rail,
    // e.g. on mobile). Deferred and un-animated to avoid a visible jump and to keep
    if (this.#activeTabId) {
      queueMicrotask(() => this.#scrollToTab(this.#activeTabId, 'auto'));
    }
  }

  #scrollToTab(tabId, behavior = 'smooth') {
    const target = this.querySelector(`button.testimonial-tabs__btn[data-tab-id="${CSS.escape(tabId)}"]`);
    if (!target) return;
    // Constrain scrolling to the rail so the page itself never scrolls.
    scrollIntoView(target, { ancestor: this.querySelector('[data-rail]'), behavior, inline: 'center' });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  get #triggers() {
    return Array.from(this.querySelectorAll('button.testimonial-tabs__btn[data-tab-id]'));
  }

  get #panels() {
    return Array.from(this.querySelectorAll('[data-panel]'));
  }

  #activate(tabId, { focus = false, scroll = true } = {}) {
    if (!tabId || tabId === this.#activeTabId) return;

    this.#triggers.forEach((btn) => {
      const isActive = btn.dataset.tabId === tabId;
      btn.setAttribute('aria-selected', String(isActive));
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.#panels.forEach((panel) => {
      panel.setAttribute('data-active', String(panel.dataset.tabId === tabId));
    });

    this.#activeTabId = tabId;

    if (focus) {
      const target = this.querySelector(`button.testimonial-tabs__btn[data-tab-id="${CSS.escape(tabId)}"]`);
      target?.focus();
    }
    if (scroll) this.#scrollToTab(tabId);
  }

  #onClick = (event) => {
    const tabId = event.currentTarget?.dataset.tabId;
    if (tabId) this.#activate(tabId);
  };

  #onKeydown = (event) => {
    const triggers = this.#triggers;
    if (triggers.length === 0) return;
    const currentIndex = triggers.findIndex((b) => b === event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % triggers.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = triggers.length - 1;

    if (nextIndex == null) return;
    event.preventDefault();
    this.#activate(triggers[nextIndex].dataset.tabId, { focus: true });
  };
}

if (!customElements.get('testimonial-tabs')) {
  customElements.define('testimonial-tabs', TestimonialTabs);
}
